import React, { useState } from 'react';
import { colors } from '../utils/colors';
import type { Teacher, TeacherAttendanceRecord, ActiveTab } from '../utils/types';

interface Props {
  teacher: Teacher;
  myAttendance: TeacherAttendanceRecord | null;
  onNavigate: (tab: ActiveTab) => void;
  onLogout: () => void;
}

const BellIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
    stroke={colors.textMuted} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 01-3.46 0"/>
  </svg>
);

const HomeScreen: React.FC<Props> = ({ teacher, myAttendance, onNavigate, onLogout }) => {
  const [showLogout, setShowLogout] = useState(false);

  const roleLabel = teacher.classAssigned
    ? `CLASS ${teacher.classAssigned.replace('cls', '').toUpperCase()}`
    : teacher.role === 'subject_teacher' ? 'SUBJECT TEACHER' : 'ADMIN';

  const myPct = myAttendance ? (myAttendance.status === 'present' ? 96 : myAttendance.status === 'late' ? 78 : 0) : 0;

  const monthLabel = new Date().toLocaleString('en-IN', { month: 'long', year: 'numeric' }).toUpperCase();

  return (
    <div style={{
      minHeight: '100dvh', background: '#FDFCFE',
      display: 'flex', flexDirection: 'column',
      paddingBottom: 80,
    }}>
      {/* Top bar */}
      <div style={{ padding: '56px 20px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Avatar + name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: '#C4933A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 800, color: '#fff', flexShrink: 0,
          }}>
            {teacher.name.charAt(0)}
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: colors.text, lineHeight: 1.2 }}>
              {teacher.name}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: colors.primary, letterSpacing: '0.5px', marginTop: 2 }}>
              {roleLabel}
            </div>
          </div>
        </div>

        {/* Bell */}
        <button
          onClick={() => setShowLogout(true)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
        >
          <BellIcon />
        </button>
      </div>

      {/* Stat cards */}
      <div style={{ padding: '0 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{
          background: '#E0F5F3', borderRadius: 18, padding: '20px 18px',
        }}>
          <div style={{ fontSize: 32, fontWeight: 900, color: colors.text }}>
            {myAttendance ? `${myPct}%` : '—'}
          </div>
          <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 6, fontWeight: 500 }}>
            My attendance
          </div>
        </div>
        <div style={{
          background: '#E9F5E0', borderRadius: 18, padding: '20px 18px',
        }}>
          <div style={{ fontSize: 32, fontWeight: 900, color: colors.text }}>88%</div>
          <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4, fontWeight: 500, lineHeight: 1.3 }}>
            Student presence
            <div style={{ fontSize: 11, color: colors.textLight, fontWeight: 600, marginTop: 2 }}>{monthLabel}</div>
          </div>
        </div>
      </div>

      {/* Nav cards */}
      <div style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* My attendance */}
        <button
          onClick={() => onNavigate('my-attendance')}
          style={{
            width: '100%', background: '#F5EEFA',
            border: `1.5px solid ${colors.borderLight}`,
            borderRadius: 16, padding: '18px 20px',
            display: 'flex', alignItems: 'center', gap: 16,
            cursor: 'pointer', textAlign: 'left',
            animation: 'slideUp 0.3s ease both',
          }}
        >
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: '#EEE3F7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <polyline points="16 11 18 13 22 9"/>
            </svg>
          </div>
          <span style={{ fontSize: 16, fontWeight: 700, color: colors.primaryDark }}>My attendance</span>
        </button>

        {/* Student attendance */}
        <button
          onClick={() => onNavigate('class-attendance')}
          style={{
            width: '100%', background: '#F5EEFA',
            border: `1.5px solid ${colors.borderLight}`,
            borderRadius: 16, padding: '18px 20px',
            display: 'flex', alignItems: 'center', gap: 16,
            cursor: 'pointer', textAlign: 'left',
            animation: 'slideUp 0.3s ease 0.05s both',
          }}
        >
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: '#EEE3F7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 00-3-3.87"/>
              <path d="M16 3.13a4 4 0 010 7.75"/>
            </svg>
          </div>
          <span style={{ fontSize: 16, fontWeight: 700, color: colors.primaryDark }}>Student attendance</span>
        </button>

        {/* Register complaint — coming soon */}
        <div style={{
          width: '100%', background: '#F5EEFA',
          border: `1.5px solid ${colors.borderLight}`,
          borderRadius: 16, padding: '18px 20px',
          display: 'flex', alignItems: 'center', gap: 16,
          opacity: 0.7,
          animation: 'slideUp 0.3s ease 0.1s both',
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: '#FEE8E8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="#C04040" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <line x1="18" y1="8" x2="23" y2="13"/>
              <line x1="23" y1="8" x2="18" y2="13"/>
            </svg>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: colors.primaryDark }}>Register complaint</span>
            <span style={{
              fontSize: 11, fontWeight: 700, color: '#C04040',
              background: '#FEE8E8', padding: '3px 8px', borderRadius: 20,
            }}>
              coming soon!
            </span>
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        background: '#fff', borderTop: `1px solid ${colors.borderLight}`,
        display: 'flex', justifyContent: 'space-around',
        padding: '10px 0 24px',
        zIndex: 100,
      }}>
        {[
          {
            label: 'Home', active: true,
            icon: (c: string) => (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
            ),
          },
          {
            label: 'Insights', active: false,
            icon: (c: string) => (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6"  y1="20" x2="6"  y2="14"/>
              </svg>
            ),
          },
          {
            label: 'Profile', active: false,
            icon: (c: string) => (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            ),
          },
        ].map(tab => {
          const c = tab.active ? colors.primary : colors.textLight;
          return (
            <button
              key={tab.label}
              onClick={tab.label === 'Profile' ? () => onNavigate('profile') : undefined}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                background: 'none', border: 'none', cursor: 'pointer', padding: '0 24px',
              }}
            >
              {tab.icon(c)}
              <span style={{ fontSize: 11, fontWeight: 600, color: c }}>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Logout modal */}
      {showLogout && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 999, padding: '0 24px',
          animation: 'fadeIn 0.2s ease both',
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: 28, width: '100%', maxWidth: 360,
            boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
          }}>
            <h3 style={{ margin: '0 0 8px', textAlign: 'center', fontSize: 18, fontWeight: 800, color: colors.text }}>
              Sign Out?
            </h3>
            <p style={{ margin: '0 0 22px', textAlign: 'center', fontSize: 13, color: colors.textMuted, lineHeight: 1.6 }}>
              You are signed in as <strong>{teacher.name}</strong>.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowLogout(false)}
                style={{
                  flex: 1, padding: '13px',
                  border: `1.5px solid ${colors.border}`, borderRadius: 12,
                  background: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: colors.textMuted,
                }}
              >
                Cancel
              </button>
              <button
                onClick={onLogout}
                style={{
                  flex: 1, padding: '13px',
                  border: 'none', borderRadius: 12,
                  background: colors.primaryDark, fontSize: 14,
                  fontWeight: 700, cursor: 'pointer', color: '#fff',
                }}
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeScreen;
